import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  type SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Router } from '@angular/router';
import {
  CompanyListSource,
  CompanyNavigationService,
  CompanyService,
  LastOpened,
  NotificationService,
  notificationDuration,
} from '@app/core';
import {
  Company,
  CompanyContext,
  companyColumns,
  companyCommands,
  contextQueryParams,
  ListType,
  SearchState,
  serializeSearchState,
  TableColumn,
  toTableColumns,
} from '@app/model';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LocalSort } from '../../util/local-sort';
import { AppMenuItem } from '../../util/menu-item';
import { ColumnResizeEvent } from '../table/column-resizer.directive';
import { ContextMenuComponent } from '../table/context-menu.component';
import { RowSelection, restoreLastOpened } from '../table/endless-rows';
import { VirtualRowsDirective } from '../table/virtual-rows.directive';

/** Moves the visible columns into the order the user dropped them in. */
export const applyVisibleOrder = (
  columns: TableColumn[],
  visibleInOrder: TableColumn[]
): TableColumn[] => {
  let next = 0;
  return columns.map((column) =>
    column.visible ? visibleInOrder[next++] : column
  );
};

@Component({
  selector: 'app-companies-search',
  templateUrl: './companies-search.component.html',
  styleUrls: ['./companies-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CompaniesSearchComponent implements OnChanges, OnDestroy {
  private changeDetector = inject(ChangeDetectorRef);
  private companyService = inject(CompanyService);
  private translateService = inject(TranslateService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private navigation = inject(CompanyNavigationService);

  /** The search of the URL: filter and sorting. */
  @Input() state: SearchState | null = null;
  /** The companies of that search, kept by CompanyNavigationService. */
  @Input() source: CompanyListSource<Company> | null = null;
  @Input() filterVisible = true;
  /** Filter or sorting changed; the page puts it into the URL. */
  @Output() stateChange = new EventEmitter<SearchState>();
  /** The filter the builder shows: the chips of the query bar of the page. */
  @Input() builderFilter: unknown[] | null = null;
  /** The builder changed its filter; the page puts it into the chips. */
  @Output() builderChange = new EventEmitter<unknown[] | null>();
  /** Enter in a field of the builder: the page searches. */
  @Output() builderSubmit = new EventEmitter<void>();
  @ViewChild('headerMenu') headerMenu?: ContextMenuComponent;
  @ViewChild('rowMenu') rowMenu?: ContextMenuComponent;
  @ViewChild(VirtualRowsDirective) rows?: VirtualRowsDirective;

  /** All columns that can be shown, in their current order. */
  public columns: TableColumn[] = toTableColumns(companyColumns).filter(
    (column) => column.visible || column.showInColumnChooser
  );
  public visibleColumns = this.columns.filter((column) => column.visible);
  public chooserColumns = this.columns.filter(
    (column) => column.showInColumnChooser && column.field
  );
  public companies: CompanyListSource<Company> | null = null;
  /** The search, which the company links carry. */
  public context: CompanyContext | null = null;
  public contextQueryParams: Record<string, string> = {};
  public lists$ = this.companyService.manualLists$;

  public readonly selection = new RowSelection<Company>(
    (company) => company.business_id
  );
  public contextCompany: Company | null = null;
  public rowMenuItems: AppMenuItem[] = [];
  public headerMenuItems: AppMenuItem[] = [];
  public sort: LocalSort | null = null;
  /** A header drag is on, so the click that ends it does not sort. */
  public dragging = false;
  public listLoading = false;
  public encodeURIComponent = encodeURIComponent;

  /** The company opened last from the search, highlighted on the way back. */
  public lastOpenedId: string | null = null;

  private subscriptions: Subscription[] = [];
  /** The way back from a company: the row to show once the rows are there. */
  private restorePending: LastOpened | null = null;
  private initialized = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['state']) {
      const state = this.state;
      this.sort = state?.sort ?? null;
      this.context = state
        ? { kind: 'search', state: serializeSearchState(state) }
        : null;
      this.contextQueryParams = contextQueryParams(this.context);
      // Another search shows other rows, the selection went with them.
      this.selection.clear();
      const last = this.context
        ? this.navigation.getLastOpened(this.context)
        : null;
      this.lastOpenedId = last?.id ?? null;
      if (!this.initialized) {
        // Only on the way back; a new search starts at the top.
        this.restorePending = last;
      } else {
        this.restorePending = null;
        this.rows?.scrollToTop();
      }
      this.initialized = true;
    }
    if (changes['source']) {
      this.companies = this.source;
      this.subscriptions.forEach((x) => x.unsubscribe());
      // The source sets its rows before the loading ends.
      this.subscriptions = this.source
        ? [
            this.source.loading$.subscribe((isLoading) => {
              this.listLoading = isLoading;
              this.changeDetector.markForCheck();
              if (!isLoading) {
                Promise.resolve().then(() => this.restore());
              }
            }),
            this.source.loadingMore$.subscribe(() =>
              this.changeDetector.markForCheck()
            ),
          ]
        : [];
    }
  }

  public get selectedCompanies() {
    return this.selection.rows;
  }

  public get selectedCompanyKeys() {
    return this.selection.rows.map((company) => company.business_id);
  }

  /** Scrolls the company opened last into view once its rows are loaded. */
  private restore() {
    const last = this.restorePending;
    const source = this.companies;
    const rows = this.rows;
    if (!last || !source || !rows || source.loading || !source.isLoaded) {
      return;
    }
    this.restorePending = null;
    restoreLastOpened(source, rows, last, () =>
      this.changeDetector.detectChanges()
    ).catch(() => undefined);
  }

  /** The table asks for more rows while the user scrolls to its end. */
  loadMore() {
    this.companies?.loadMore().then(() => this.changeDetector.markForCheck());
  }

  retryMore() {
    this.companies?.clearError();
    this.loadMore();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
    if (this.context && this.rows) {
      this.navigation.setScrollOffset(this.context, this.rows.scrollOffset);
    }
  }

  private emitState(change: Partial<SearchState>) {
    this.stateChange.emit({
      filterValue: this.state?.filterValue ?? null,
      sort: this.sort
        ? { field: this.sort.field, order: this.sort.order === -1 ? -1 : 1 }
        : null,
      ...change,
    });
  }

  filterChanged(filter: unknown[] | null) {
    // A new filter starts at the top, the sorting is kept.
    this.emitState({ filterValue: filter });
  }

  listClicked(list: ListType) {
    this.companyService
      .addCompanyToList(this.selectedCompanyKeys, list.id)
      .subscribe({
        next: () =>
          this.notificationService.notify(
            'Companies added',
            'success',
            notificationDuration.success
          ),
        error: (e) =>
          this.notificationService.notify(
            e.message,
            'error',
            notificationDuration.error
          ),
      });
  }

  onRowClick(event: MouseEvent, company: Company, index: number) {
    const target = event.target as HTMLElement;
    // Clicks into the selection column only select; links do it themselves.
    if (target.closest('.selection-cell') || target.closest('a')) {
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      this.openInNewTab(company, index);
    } else {
      this.router.navigate(this.companyCommands(company), {
        queryParams: this.rowQueryParams(index),
      });
    }
  }

  /** The middle button opens the company in a new tab, as on a link. */
  onRowAuxClick(event: MouseEvent, company: Company, index: number) {
    const target = event.target as HTMLElement;
    if (
      event.button === 1 &&
      !target.closest('.selection-cell') &&
      !target.closest('a')
    ) {
      event.preventDefault();
      this.openInNewTab(company, index);
    }
  }

  private companyCommands(company: Company) {
    return companyCommands(company.business_id, 'overview', this.context);
  }

  /** The context and the index of the row in the whole list. */
  private rowQueryParams(index: number) {
    return { ...this.contextQueryParams, row: String(index) };
  }

  openInNewTab(company: Company, index: number) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(this.companyCommands(company), {
        queryParams: this.rowQueryParams(index),
      })
    );
    window.open(`#${url}`, '_blank');
  }

  // Selection by business id.

  isSelected(company: Company) {
    return this.selection.has(company);
  }

  toggleRow(company: Company) {
    this.selection.toggle(company);
  }

  /** Whether all loaded rows are selected (the header check box). */
  get allSelected() {
    return this.selection.hasAll(this.companies?.items ?? []);
  }

  /** Selects or deselects all loaded rows. */
  toggleAll() {
    this.selection.toggleAll(this.companies?.items ?? []);
  }

  // The check box handles its own clicks; the rest of the cell toggles too.
  onSelectionCellClick(company: Company) {
    this.toggleRow(company);
  }

  onRowContextMenu(event: MouseEvent, company: Company, index: number) {
    this.contextCompany = company;
    this.rowMenuItems = [
      {
        label: this.translateService.instant('common.label.openInNewTab'),
        command: () => this.openInNewTab(company, index),
      },
    ];
    this.rowMenu?.show(event);
  }

  onHeaderClick(event: Event, column: TableColumn) {
    if (!column.sortable || !column.field || this.dragging) {
      return;
    }
    const { ctrlKey, metaKey } = event as MouseEvent;
    // Ctrl + click clears the sorting.
    if (ctrlKey || metaKey) {
      this.applySort(null, 1);
    } else {
      this.applySort(
        column.field,
        this.sort?.field === column.field ? -this.sort.order : 1
      );
    }
  }

  onHeaderContextMenu(event: MouseEvent, column: TableColumn) {
    if (!column.sortable || !column.field || !this.headerMenu) {
      return;
    }
    const field = column.field;
    this.headerMenuItems = [
      {
        label: 'Sort Ascending',
        icon: 'app-icon fa-solid fa-arrow-up-short-wide',
        command: () => this.applySort(field, 1),
      },
      {
        label: 'Sort Descending',
        icon: 'app-icon fa-solid fa-arrow-down-wide-short',
        command: () => this.applySort(field, -1),
      },
      { label: 'Clear Sorting', command: () => this.applySort(null, 1) },
    ];
    this.headerMenu.show(event);
  }

  /** Single column sorting; the rows start again at the top. */
  applySort(field: string | null, order: number) {
    this.emitState({
      sort: field ? { field, order: order === -1 ? -1 : 1 } : null,
    });
  }

  /**
   * Like on the list detail page only the resized column gets a width, bound
   * in the template; its cells keep it as minimum width. The new width is the
   * width it had plus the dragged distance.
   */
  onColumnResize(event: ColumnResizeEvent) {
    const key = event.element.getAttribute('data-column');
    const shown = this.columns.find((column) => column.key === key);
    const width = Math.round(
      (shown?.width ?? event.element.offsetWidth) + event.delta
    );
    if (width < 15) {
      return;
    }
    this.columns = this.columns.map((column) =>
      column.key === key ? { ...column, width } : column
    );
    this.visibleColumns = this.columns.filter((x) => x.visible);
  }

  onColumnReorder(event: { columns?: TableColumn[] }) {
    if (event.columns) {
      this.columns = applyVisibleOrder(this.columns, event.columns);
      this.visibleColumns = this.columns.filter((x) => x.visible);
    }
  }

  onColumnDrop(event: CdkDragDrop<unknown>) {
    const moved = [...this.visibleColumns];
    moveItemInArray(moved, event.previousIndex, event.currentIndex);
    this.onColumnReorder({ columns: moved });
  }

  // The click at the end of a drag comes after cdkDragEnded.
  onColumnDragEnded() {
    setTimeout(() => (this.dragging = false));
  }

  setColumnVisible(column: TableColumn, visible: boolean) {
    this.columns = this.columns.map((x) =>
      x.key === column.key ? { ...x, visible } : x
    );
    this.visibleColumns = this.columns.filter((x) => x.visible);
    this.chooserColumns = this.columns.filter(
      (x) => x.showInColumnChooser && x.field
    );
  }

  isAltRow(rowIndex: number) {
    return rowIndex % 2 === 1;
  }
}
